import { Button } from "@repo/ui/button";
import { Checkbox } from "@repo/ui/checkbox";
import { Label } from "@repo/ui/label";
import { RadioGroup, RadioGroupItem } from "@repo/ui/radio-group";
import { Textarea } from "@repo/ui/textarea";
import { useId, useMemo, useState } from "react";

import type { AskUserAnswer, AskUserSuspendPayload } from "./types";

const OTHER_OPTION_ID = "__other__";

type Selections = Record<string, { ids: string[]; otherText: string }>;

const buildEmptySelections = (payload: AskUserSuspendPayload): Selections =>
  Object.fromEntries(
    payload.questions.map((q) => [q.id, { ids: [], otherText: "" }]),
  );

const isQuestionAnswered = (
  selection: { ids: string[]; otherText: string } | undefined,
): boolean => {
  if (!selection) return false;
  const hasOption = selection.ids.some((id) => id !== OTHER_OPTION_ID);
  const hasOther =
    selection.ids.includes(OTHER_OPTION_ID) &&
    selection.otherText.trim() !== "";
  return hasOption || hasOther;
};

const buildAnswers = (
  payload: AskUserSuspendPayload,
  selections: Selections,
): AskUserAnswer[] =>
  payload.questions.map((q) => {
    const sel = selections[q.id] ?? { ids: [], otherText: "" };
    const selectedOptionIds = sel.ids.filter((id) => id !== OTHER_OPTION_ID);
    const otherText =
      sel.ids.includes(OTHER_OPTION_ID) && sel.otherText.trim() !== ""
        ? sel.otherText.trim()
        : undefined;
    return {
      questionId: q.id,
      selectedOptionIds,
      ...(otherText !== undefined && { otherText }),
    };
  });

/**
 * Renders the multi-question form posted by the `askUser` tool inside a
 * suspended tool-call row. Each question is a single- or multi-select with an
 * optional "Other…" free-text fallback. Submit is disabled until every
 * question has at least one option selected (or a non-empty `otherText` if
 * the user picked Other).
 */
export const AskUserForm = ({
  payload,
  disabled,
  onSubmit,
  onCancel,
}: {
  payload: AskUserSuspendPayload;
  disabled?: boolean;
  onSubmit: (answers: AskUserAnswer[]) => void;
  onCancel: () => void;
}) => {
  const [selections, setSelections] = useState<Selections>(() =>
    buildEmptySelections(payload),
  );
  const formId = useId();

  const allAnswered = useMemo(
    () => payload.questions.every((q) => isQuestionAnswered(selections[q.id])),
    [payload.questions, selections],
  );

  const setOtherText = (questionId: string, value: string) => {
    setSelections((prev) => {
      const cur = prev[questionId] ?? { ids: [], otherText: "" };
      return { ...prev, [questionId]: { ...cur, otherText: value } };
    });
  };

  const setSingleSelection = (questionId: string, optionId: string) => {
    setSelections((prev) => {
      const cur = prev[questionId] ?? { ids: [], otherText: "" };
      return {
        ...prev,
        [questionId]: { ...cur, ids: [optionId] },
      };
    });
  };

  const toggleMultiSelection = (
    questionId: string,
    optionId: string,
    checked: boolean,
  ) => {
    setSelections((prev) => {
      const cur = prev[questionId] ?? { ids: [], otherText: "" };
      const ids = checked
        ? [...cur.ids.filter((id) => id !== optionId), optionId]
        : cur.ids.filter((id) => id !== optionId);
      return { ...prev, [questionId]: { ...cur, ids } };
    });
  };

  return (
    <form
      className="flex flex-col gap-4 p-3 pt-0"
      onSubmit={(e) => {
        e.preventDefault();
        if (!allAnswered || disabled) return;
        onSubmit(buildAnswers(payload, selections));
      }}
    >
      {payload.title && (
        <p className="font-medium text-foreground text-xs/relaxed">
          {payload.title}
        </p>
      )}

      {payload.questions.map((question, qIndex) => {
        const selection = selections[question.id] ?? {
          ids: [],
          otherText: "",
        };
        const showOther = question.allowOther !== false;
        const otherChecked = selection.ids.includes(OTHER_OPTION_ID);

        return (
          <div key={question.id} className="flex flex-col gap-2">
            <p className="font-medium text-foreground text-xs/relaxed">
              {payload.questions.length > 1 && (
                <span className="mr-1.5 text-muted-foreground">
                  {qIndex + 1}.
                </span>
              )}
              {question.prompt}
            </p>

            {question.allowMultiple ? (
              <div className="flex flex-col gap-2">
                {question.options.map((option) => {
                  const id = `${formId}-${question.id}-${option.id}`;
                  const checked = selection.ids.includes(option.id);
                  return (
                    <Label
                      key={option.id}
                      htmlFor={id}
                      className="cursor-pointer"
                    >
                      <Checkbox
                        id={id}
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={(value: boolean) =>
                          toggleMultiSelection(
                            question.id,
                            option.id,
                            value === true,
                          )
                        }
                      />
                      {option.label}
                    </Label>
                  );
                })}
                {showOther && (
                  <Label
                    htmlFor={`${formId}-${question.id}-${OTHER_OPTION_ID}`}
                    className="cursor-pointer"
                  >
                    <Checkbox
                      id={`${formId}-${question.id}-${OTHER_OPTION_ID}`}
                      checked={otherChecked}
                      disabled={disabled}
                      onCheckedChange={(value: boolean) =>
                        toggleMultiSelection(
                          question.id,
                          OTHER_OPTION_ID,
                          value === true,
                        )
                      }
                    />
                    Other…
                  </Label>
                )}
              </div>
            ) : (
              <RadioGroup
                value={selection.ids[0] ?? ""}
                onValueChange={(value: string) =>
                  setSingleSelection(question.id, value)
                }
                disabled={disabled}
              >
                {question.options.map((option) => {
                  const id = `${formId}-${question.id}-${option.id}`;
                  return (
                    <Label
                      key={option.id}
                      htmlFor={id}
                      className="cursor-pointer"
                    >
                      <RadioGroupItem id={id} value={option.id} />
                      {option.label}
                    </Label>
                  );
                })}
                {showOther && (
                  <Label
                    htmlFor={`${formId}-${question.id}-${OTHER_OPTION_ID}`}
                    className="cursor-pointer"
                  >
                    <RadioGroupItem
                      id={`${formId}-${question.id}-${OTHER_OPTION_ID}`}
                      value={OTHER_OPTION_ID}
                    />
                    Other…
                  </Label>
                )}
              </RadioGroup>
            )}

            {showOther && otherChecked && (
              <Textarea
                value={selection.otherText}
                disabled={disabled}
                placeholder="Type your answer"
                onChange={(e) => setOtherText(question.id, e.target.value)}
                className="min-h-12"
              />
            )}
          </div>
        );
      })}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={disabled || !allAnswered}>
          Submit
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={onCancel}
        >
          Skip
        </Button>
      </div>
    </form>
  );
};
